import React, { useState, useEffect } from 'react';
import {
  Box,
  SimpleGrid,
  Text,
  Badge,
  VStack,
  HStack,
  useColorModeValue,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Divider,
  Flex,
  Spinner,
  Center,
  Select,
  Button,
  useToast,
} from '@chakra-ui/react';
import { tableService } from '../../../../services/tableService';
import { employeeService } from '../../../../services/employeeService';
import { floorService } from '../../../../services/floorService';
import Card from "../../../../components/card/Card";
import { motion } from 'framer-motion';
import { Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';

// Durum renkleri ve öncelikleri
const STATUS_CONFIG = {
  pending: { color: 'gray', priority: 1, label: 'Beklemede', borderColor: '#718096' },
  preparing: { color: 'yellow', priority: 2, label: 'Hazırlanıyor', borderColor: '#D69E2E' },
  ready: { color: 'blue', priority: 3, label: 'Hazır', borderColor: '#3182CE' },
  delivered: { color: 'green', priority: 4, label: 'Teslim Edildi', borderColor: '#38A169' },
  cancelled: { color: 'red', priority: 0, label: 'İptal Edildi', borderColor: '#E53E3E' }
};

// Backend'den gelen Türkçe status'u İngilizce'ye çevir
const normalizeStatus = (status) => {
  if (!status) return status;
  const statusMap = {
    'Beklemede': 'pending',
    'Hazırlanıyor': 'preparing',
    'Hazır': 'ready',
    'Teslim Edildi': 'delivered',
    'İptal Edildi': 'cancelled'
  };
  return statusMap[status] || status; // Eğer Türkçe değilse olduğu gibi döndür
};

export default function TableBasedOrdersView({ orders = [], onStatusChange, isLoading }) {
  const [floors, setFloors] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [tables, setTables] = useState([]);
  const [groupedOrders, setGroupedOrders] = useState({});
  const [selectedTable, setSelectedTable] = useState(null);
  const [employees, setEmployees] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  
  const bgColor = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.50', 'navy.700');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const summaryBg = useColorModeValue('gray.50', 'navy.700');

  // Floor'ları getir
  useEffect(() => {
    const fetchFloors = async () => {
      try {
        const response = await floorService.getFloors();
        if (response.success && response.data) {
          setFloors(response.data);
          if (response.data.length > 0 && !selectedFloor) {
            setSelectedFloor(response.data[0]);
          }
        }
      } catch (error) {
        console.error('Katlar yüklenirken hata:', error);
      }
    };
    fetchFloors();
  }, []);

  // TÜM masaları getir (tüm floor'lar için sipariş sayısını hesaplamak için)
  useEffect(() => {
    const fetchAllTables = async () => {
      try {
        // floorId olmadan çağırırsak tüm masaları getirir
        const response = await tableService.getTables();
        if (response && Array.isArray(response)) {
          setTables(response);
        } else if (response?.data && Array.isArray(response.data)) {
          setTables(response.data);
        }
      } catch (error) {
        console.error('Masalar yüklenirken hata:', error);
      }
    };
    fetchAllTables();
  }, []);

  // Çalışanları getir
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await employeeService.getEmployees();
        if (response && response.success) {
          setEmployees(response.data || []);
        } else if (Array.isArray(response)) {
          setEmployees(response);
        }
      } catch (error) {
        console.error('Çalışanlar yüklenirken hata:', error);
      }
    };
    fetchEmployees();
  }, []);

  // Siparişleri masa bazında grupla - Backend'den gelen veriyi kullan ama optimistic update'leri koru
  useEffect(() => {
    if (!Array.isArray(orders) || !tables.length) return;

    setGroupedOrders(prev => {
      const grouped = {};
      
      // Önce tüm masaları ekle (siparişi olmasa bile)
      tables.forEach(table => {
        grouped[table._id || table.id] = {
          table: table,
          orders: []
        };
      });

      // Siparişleri masalara dağıt - Backend'den gelen veriyi kullan ama mevcut state'teki optimistic update'leri koru
      orders.forEach(order => {
        // Status'u normalize et (Türkçe'den İngilizce'ye çevir)
        const normalizedStatus = normalizeStatus(order.status);
        const normalizedOrder = { ...order, status: normalizedStatus };
        
        // tableId farklı formatlarda gelebilir: obje, string, veya populate edilmiş
        let tableId = null;
        
        if (order.tableId) {
          if (typeof order.tableId === 'object') {
            tableId = order.tableId._id || order.tableId.id || order.tableId.toString();
          } else {
            tableId = order.tableId.toString();
          }
        } else if (order.table) {
          // Alternatif field adı
          if (typeof order.table === 'object') {
            tableId = order.table._id || order.table.id || order.table.toString();
          } else {
            tableId = order.table.toString();
          }
        }
        
        if (tableId && grouped[tableId]) {
          // Mevcut state'te bu siparişin optimistic update'i var mı kontrol et
          const prevTableData = prev[tableId];
          const existingOrder = prevTableData?.orders?.find(
            o => {
              const oId = o._id || o.id;
              const orderId = normalizedOrder._id || normalizedOrder.id;
              return oId && orderId && (oId.toString() === orderId.toString());
            }
          );
          
          if (existingOrder) {
            // Eğer mevcut state'te bu sipariş varsa, optimistic update'i koru
            // Ama backend'den gelen diğer bilgileri de merge et
            grouped[tableId].orders.push({
              ...normalizedOrder,
              status: existingOrder.status, // Optimistic update'teki status'u koru
              deliveredBy: existingOrder.deliveredBy // Optimistic update'teki deliveredBy'ı koru
            });
          } else {
            // Yeni sipariş veya mevcut state'te yok, backend'den gelen veriyi kullan
            grouped[tableId].orders.push(normalizedOrder);
          }
        } else if (tableId) {
          // Eğer masa listede yoksa, siparişten masa bilgisini oluştur
          const tableName = order.tableId?.displayName || order.tableId?.name || order.tableName || 'Bilinmeyen Masa';
          grouped[tableId] = {
            table: {
              _id: tableId,
              id: tableId,
              name: tableName,
              displayName: tableName,
              capacity: order.tableId?.capacity || null
            },
            orders: [normalizedOrder]
          };
        }
      });

      return grouped;
    });
  }, [orders, tables]);

  // Bir masanın en yüksek öncelikli durumunu bul
  const getTableStatus = (tableOrders) => {
    if (!tableOrders || tableOrders.length === 0) return null;
    
    let highestPriority = -1;
    let highestStatus = null;
    
    tableOrders.forEach(order => {
      const status = order.status;
      const config = STATUS_CONFIG[status];
      if (config && config.priority > highestPriority) {
        highestPriority = config.priority;
        highestStatus = status;
      }
    });
    
    return highestStatus ? STATUS_CONFIG[highestStatus] : null;
  };

  // Masa kartına tıklandığında
  const handleTableClick = (tableId) => {
    const tableData = groupedOrders[tableId];
    if (tableData) {
      // Güncel siparişleri al (delivered ve cancelled hariç)
      const activeOrders = tableData.orders.filter(
        order => order.status !== 'delivered' && order.status !== 'cancelled'
      );
      setSelectedTable({
        ...tableData,
        orders: activeOrders
      });
      onOpen();
    }
  };

  // Drawer kapanırken sadece kapat, selectedTable'ı temizleme (bir sonraki açılışta güncel veri gelecek)
  const handleDrawerClose = () => {
    // Pending delivery state'ini temizle
    if (selectedTable?.pendingDelivery) {
      setSelectedTable(prev => {
        const { pendingDelivery, ...rest } = prev;
        return rest;
      });
    }
    onClose();
  };

  // Drawer'da sipariş durumu güncelle
  const handleOrderStatusChange = async (orderId, newStatus, deliveredBy = null) => {
    try {
      // Önce UI'ı güncelle (optimistic update)
      if (selectedTable) {
        const tableId = selectedTable.table._id || selectedTable.table.id;
        
        // selectedTable state'ini hemen güncelle (popup'ta görünsün)
        setSelectedTable(prev => {
          if (!prev) return prev;
          if (newStatus === 'delivered' || newStatus === 'cancelled') {
            // Siparişi listeden çıkar
            return {
              ...prev,
              orders: prev.orders.filter(
                order => (order._id !== orderId && order.id !== orderId)
              )
            };
          } else {
            // Sipariş durumunu hemen güncelle
            return {
              ...prev,
              orders: prev.orders.map(order => {
                if (order._id === orderId || order.id === orderId) {
                  return { ...order, status: newStatus, deliveredBy };
                }
                return order;
              })
            };
          }
        });
        
        // Ana gruplamayı hemen güncelle (optimistic update)
        setGroupedOrders(prev => {
          const updated = { ...prev };
          if (updated[tableId]) {
            if (newStatus === 'delivered' || newStatus === 'cancelled') {
              // Siparişi listeden çıkar
              updated[tableId] = {
                ...updated[tableId],
                orders: updated[tableId].orders.filter(
                  order => (order._id !== orderId && order.id !== orderId)
                )
              };
            } else {
              // Sipariş durumunu hemen güncelle
              updated[tableId] = {
                ...updated[tableId],
                orders: updated[tableId].orders.map(order => {
                  if (order._id === orderId || order.id === orderId) {
                    return { ...order, status: newStatus, deliveredBy };
                  }
                  return order;
                })
              };
            }
          }
          return updated;
        });
      }
      
      // Backend'e gönder
      await onStatusChange(orderId, newStatus, deliveredBy);
      
      // Backend'den gelen güncel veriyi bekle ve tekrar güncelle
      // (orders prop'u değiştiğinde useEffect otomatik güncelleyecek)
      
      // Pending delivery state'ini temizle
      if (selectedTable?.pendingDelivery) {
        setSelectedTable(prev => {
          const { pendingDelivery, ...rest } = prev;
          return rest;
        });
      }
      
      // Eğer durum "delivered" veya "cancelled" ise ve masada başka sipariş kalmadıysa drawer'ı kapat
      if (selectedTable && (newStatus === 'delivered' || newStatus === 'cancelled')) {
        const tableId = selectedTable.table._id || selectedTable.table.id;
        setTimeout(() => {
          setGroupedOrders(current => {
            const currentTableData = current[tableId];
            const remainingOrders = currentTableData?.orders?.filter(
              order => order.status !== 'delivered' && order.status !== 'cancelled'
            ) || [];
            
            if (remainingOrders.length === 0) {
              onClose();
            }
            return current;
          });
        }, 100);
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast({
        title: "Hata",
        description: "Sipariş durumu güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
        isClosable: true
      });
      
      // Hata durumunda orders prop'u değiştiğinde useEffect otomatik düzeltecek
    }
  };

  // Sipariş toplam tutarını hesapla
  const calculateOrderTotal = (order) => {
    return order.totalAmount || 0;
  };

  // Sipariş item sayısını hesapla
  const getOrderItemCount = (order) => {
    return order.items?.reduce((total, item) => total + item.quantity, 0) || 0;
  };



  // TÜM masaları göster (aktif siparişi olanlar + olmayanlar)
  const allTables = React.useMemo(() => {
    const tablesMap = {};
    
    // Önce tüm masaları ekle
    tables.forEach(table => {
      tablesMap[table._id || table.id] = {
        table: table,
        orders: []
      };
    });
    
    // Sonra siparişleri ekle
    orders.forEach(order => {
      const normalizedStatus = normalizeStatus(order.status);
      const normalizedOrder = { ...order, status: normalizedStatus };
      
      let tableId = null;
      if (order.tableId) {
        if (typeof order.tableId === 'object') {
          tableId = order.tableId._id || order.tableId.id || order.tableId.toString();
        } else {
          tableId = order.tableId.toString();
        }
      } else if (order.table) {
        if (typeof order.table === 'object') {
          tableId = order.table._id || order.table.id || order.table.toString();
        } else {
          tableId = order.table.toString();
        }
      }
      
      if (tableId && tablesMap[tableId]) {
        // Sadece aktif siparişleri ekle (delivered ve cancelled hariç)
        if (normalizedStatus !== 'delivered' && normalizedStatus !== 'cancelled') {
          tablesMap[tableId].orders.push(normalizedOrder);
        }
      }
    });
    
    return Object.values(tablesMap);
  }, [tables, orders]);

  // Aktif siparişi olan masaları filtrele (sadece gösterim için)
  const tablesWithOrders = React.useMemo(() => {
    return allTables.filter(group => group.orders && group.orders.length > 0);
  }, [allTables]);

  // Nakit ödeme gereken siparişleri filtrele
  const cashPaymentOrders = React.useMemo(() => {
    const allActiveOrders = tablesWithOrders.flatMap(group => group.orders);
    return allActiveOrders.filter(order => 
      order.paymentMethod === 'restaurant' || !order.paymentMethod
    );
  }, [tablesWithOrders]);

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner size="xl" color="#e45858" />
      </Center>
    );
  }

  // Tüm masaları göster (aktif sipariş olsun veya olmasın)
  // if (allTables.length === 0) {
  //   return (
  //     <Center py={10}>
  //       <Text fontSize="lg" color={textColor}>
  //         Henüz masa eklenmemiş
  //       </Text>
  //     </Center>
  //   );
  // }

  // Tüm masalar için pulse animasyonlarını oluştur
  const pulseAnimations = tablesWithOrders.map(({ table, orders: tableOrders }) => {
    const statusConfig = getTableStatus(tableOrders);
    const borderColorValue = statusConfig?.borderColor || borderColor;
    const orderCount = tableOrders.length;
    
    if (orderCount > 0) {
      return `
        @keyframes pulse-glow-${table._id || table.id} {
          0%, 100% {
            box-shadow: 0 0 20px ${borderColorValue}80, 0 0 40px ${borderColorValue}40;
          }
          50% {
            box-shadow: 0 0 30px ${borderColorValue}AA, 0 0 60px ${borderColorValue}60;
          }
        }
      `;
    }
    return '';
  }).join('');

  return (
    <Box p={4}>
      <style>
        {pulseAnimations}
      </style>
      
      {/* Nakit Ödeme Uyarı Banner'ı */}
      {cashPaymentOrders && cashPaymentOrders.length > 0 && (
        <Box mb={4}>
          <Flex
            direction="column"
            bg="orange.50"
            border="1px solid"
            borderColor="orange.200"
            borderRadius="12px"
            p={4}
            gap={2}
          >
            <Flex align="center" gap={2}>
              <Box
                as="span"
                fontSize="20px"
              >
                ⚠️
              </Box>
              <Text fontWeight="bold" color="orange.700" fontSize="sm">
                Nakit Ödeme Gereken Siparişler
              </Text>
            </Flex>
            <VStack align="stretch" spacing={2} mt={2}>
              {cashPaymentOrders.map((order) => {
                const tableName = order.tableId?.displayName || order.tableId?.name || order.tableName || 'Bilinmeyen Masa';
                const orderDate = new Date(order.createdAt).toLocaleTimeString('tr-TR');
                return (
                  <Flex
                    key={order._id || order.id}
                    align="center"
                    justify="space-between"
                    bg="white"
                    p={3}
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="orange.300"
                  >
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="semibold" color="orange.700">
                        {tableName}, {orderDate} tarihinde verilen sipariş nakit ödeme olacaktır
                      </Text>
                    </VStack>
                    <Text fontSize="xs" fontWeight="bold" color="orange.600">
                      {calculateOrderTotal(order).toFixed(2)} ₺
                    </Text>
                  </Flex>
                );
              })}
            </VStack>
            <Text fontSize="xs" color="orange.600" mt={2} fontWeight="medium">
              💰 Ödemenin alındığından emin olun!
            </Text>
          </Flex>
        </Box>
      )}
      {/* Floor Tabs ile Masalar Grid Görünümü */}
      {floors.length > 0 ? (
        <Tabs
          index={floors.findIndex(f => f._id === selectedFloor?._id)}
          onChange={(index) => setSelectedFloor(floors[index])}
        >
          <TabList mb={4}>
            {floors.map((floor) => {
              // Bu floor'daki aktif sipariş sayısını hesapla
              const floorTables = allTables.filter(t => {
                const tableFloorId = t.table.floor?._id || t.table.floor || null;
                const floorId = floor._id?.toString() || floor._id;
                
                // Floor ID'leri string olarak karşılaştır
                if (tableFloorId && floorId) {
                  return String(tableFloorId) === String(floorId);
                }
                // Floor'u olmayan masalar ilk floor'a ait
                if (!tableFloorId && floors.length > 0) {
                  return String(floors[0]._id) === String(floorId);
                }
                return false;
              });
              const floorOrderCount = floorTables.reduce((sum, t) => sum + (t.orders?.length || 0), 0);
              
              return (
                <Tab key={floor._id}>
                  <Flex align="center" gap={2}>
                    <Text>{floor.displayName}</Text>
                    {floorOrderCount > 0 && (
                      <Badge colorScheme="red" borderRadius="full" px={2}>
                        {floorOrderCount}
                      </Badge>
                    )}
                  </Flex>
                </Tab>
              );
            })}
          </TabList>
          
          <TabPanels>
            {floors.map((floor) => {
              // Bu floor'a ait masaları filtrele
              const floorTables = allTables.filter(t => {
                const tableFloorId = t.table.floor?._id || t.table.floor || null;
                const floorId = floor._id?.toString() || floor._id;
                
                // Floor ID'leri string olarak karşılaştır
                if (tableFloorId && floorId) {
                  return String(tableFloorId) === String(floorId);
                }
                // Floor'u olmayan masalar ilk floor'a ait
                if (!tableFloorId && floors.length > 0) {
                  return String(floors[0]._id) === String(floorId);
                }
                return false;
              });
              
              return (
                <TabPanel key={floor._id} p={0}>
                  <Box>
                    {floorTables.length > 0 ? (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 5 }} spacing={6}>
                        {floorTables.map(({ table, orders: tableOrders }) => {
            const statusConfig = getTableStatus(tableOrders);
            const orderCount = tableOrders.length;
            const tableId = table._id || table.id;
            const hasActiveOrders = orderCount > 0;
            
            return (
              <motion.div
                key={tableId}
                whileHover={hasActiveOrders ? { scale: 1.05 } : {}}
                whileTap={hasActiveOrders ? { scale: 0.95 } : {}}
              >
                <Card
                  p={4}
                  cursor={hasActiveOrders ? 'pointer' : 'default'}
                  onClick={() => {
                    if (hasActiveOrders) {
                      handleTableClick(tableId);
                    }
                  }}
                  borderWidth="2px"
                  borderColor={hasActiveOrders ? (statusConfig?.borderColor || borderColor) : borderColor}
                  bg={hasActiveOrders ? bgColor : hoverBg}
                  opacity={hasActiveOrders ? 1 : 0.7}
                  position="relative"
                  boxShadow={hasActiveOrders ? `0 0 20px ${statusConfig?.borderColor || 'gray'}` : 'none'}
                  _hover={hasActiveOrders ? {
                    boxShadow: `0 0 25px ${statusConfig?.borderColor || 'gray'}`,
                  } : {}}
                >
                  <VStack spacing={2} align="stretch">
                    {/* Durum Badge */}
                    {hasActiveOrders && statusConfig && (
                      <Badge
                        colorScheme={statusConfig.color}
                        fontSize="xs"
                        px={2}
                        py={0.5}
                        maxW="100px"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        alignSelf="flex-start"
                      >
                        {statusConfig.label}
                      </Badge>
                    )}
                    
                    {/* Masa Adı */}
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color={textColor}
                      noOfLines={1}
                    >
                      {table.displayName || table.name || 'Masa'}
                    </Text>
                    
                    {/* Kapasite */}
                    {table.capacity && (
                      <HStack spacing={1}>
                        <Text fontSize="sm" color="gray.500">
                          👥 {table.capacity} kişi
                        </Text>
                      </HStack>
                    )}
                    
                    {/* Sipariş Özeti */}
                    {hasActiveOrders && (
                      <Box
                        mt={2}
                        p={2}
                        bg={summaryBg}
                        borderRadius="md"
                      >
                        <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                          {orderCount} aktif sipariş
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Toplam: {tableOrders.reduce((sum, o) => sum + getOrderItemCount(o), 0)} ürün
                        </Text>
                      </Box>
                    )}
                    
                    {/* Sipariş Yok */}
                    {!hasActiveOrders && (
                      <Text fontSize="sm" color="gray.400" fontStyle="italic">
                        Sipariş yok
                      </Text>
                    )}
                  </VStack>
                </Card>
              </motion.div>
            );
          })}
        </SimpleGrid>
                    ) : (
                      <Center py={10}>
                        <Text fontSize="lg" color={textColor}>
                          Bu katta henüz masa yok
                        </Text>
                      </Center>
                    )}
      </Box>
                </TabPanel>
              );
            })}
          </TabPanels>
        </Tabs>
      ) : (
        <Box>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 5 }} spacing={6}>
          {allTables.map(({ table, orders: tableOrders }) => {
            const statusConfig = getTableStatus(tableOrders);
            const orderCount = tableOrders.length;
            const tableId = table._id || table.id;
            const hasActiveOrders = orderCount > 0;
            
            return (
              <motion.div
                key={tableId}
                whileHover={hasActiveOrders ? { scale: 1.05 } : {}}
                whileTap={hasActiveOrders ? { scale: 0.95 } : {}}
              >
                <Card
                  p={4}
                  cursor={hasActiveOrders ? 'pointer' : 'default'}
                  onClick={() => {
                    if (hasActiveOrders) {
                      handleTableClick(tableId);
                    }
                  }}
                  borderWidth="2px"
                  borderColor={hasActiveOrders ? (statusConfig?.borderColor || borderColor) : borderColor}
                  bg={hasActiveOrders ? bgColor : hoverBg}
                  opacity={hasActiveOrders ? 1 : 0.7}
                  position="relative"
                  boxShadow={hasActiveOrders ? `0 0 20px ${statusConfig?.borderColor || 'gray'}` : 'none'}
                  _hover={hasActiveOrders ? {
                    boxShadow: `0 0 25px ${statusConfig?.borderColor || 'gray'}`,
                  } : {}}
                >
                  <VStack spacing={2} align="stretch">
                    {/* Durum Badge */}
                    {hasActiveOrders && statusConfig && (
                      <Badge
                        colorScheme={statusConfig.color}
                        fontSize="xs"
                        px={2}
                        py={0.5}
                        maxW="100px"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        alignSelf="flex-start"
                      >
                        {statusConfig.label}
                      </Badge>
                    )}
                    
                    {/* Masa Adı */}
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color={textColor}
                      noOfLines={1}
                    >
                      {table.displayName || table.name || 'Masa'}
                    </Text>
                    
                    {/* Kapasite */}
                    {table.capacity && (
                      <HStack spacing={1}>
                        <Text fontSize="sm" color="gray.500">
                          👥 {table.capacity} kişi
                        </Text>
                      </HStack>
                    )}
                    
                    {/* Sipariş Özeti */}
                    {hasActiveOrders && (
                      <Box
                        mt={2}
                        p={2}
                        bg={summaryBg}
                        borderRadius="md"
                      >
                        <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                          {orderCount} aktif sipariş
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          Toplam: {tableOrders.reduce((sum, o) => sum + getOrderItemCount(o), 0)} ürün
                        </Text>
                      </Box>
                    )}
                    
                    {/* Sipariş Yok */}
                    {!hasActiveOrders && (
                      <Text fontSize="sm" color="gray.400" fontStyle="italic">
                        Sipariş yok
                      </Text>
                    )}
            </VStack>
                </Card>
              </motion.div>
            );
          })}
        </SimpleGrid>
        </Box>
      )}

      {/* Yatay Drawer - Sipariş Detayları */}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleDrawerClose}
        size={{ base: "full", sm: "xl" }}
      >
        <DrawerOverlay 
          backdropFilter="blur(4px)"
          bg="blackAlpha.300"
        />
        <DrawerContent
          borderTopLeftRadius={{ base: "0", sm: "xl" }}
          borderBottomLeftRadius={{ base: "0", sm: "xl" }}
        >
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            {selectedTable && (
              <VStack align="stretch" spacing={2}>
                <Text fontSize={{ base: "xl", sm: "2xl" }} fontWeight="bold">
                  {selectedTable.table.displayName || selectedTable.table.name || 'Masa'}
                </Text>
                {selectedTable.table.capacity && (
                  <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.500">
                    Kapasite: {selectedTable.table.capacity} kişi
                  </Text>
                )}
              </VStack>
            )}
          </DrawerHeader>

          <DrawerBody>
            {selectedTable && (() => {
              // Her zaman güncel groupedOrders'dan veriyi al
              const tableId = selectedTable.table._id || selectedTable.table.id;
              const currentTableData = groupedOrders[tableId];
              const activeOrders = currentTableData?.orders?.filter(
                order => order.status !== 'delivered' && order.status !== 'cancelled'
              ) || [];
              
              return activeOrders.length > 0 ? (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {activeOrders.map((order, index) => {
                  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
                  const itemCount = getOrderItemCount(order);
                  const orderTotal = calculateOrderTotal(order);

                  return (
                    <Box
                      key={order._id || order.id || index}
                      p={4}
                      borderWidth="2px"
                      borderColor={statusConfig.borderColor}
                      borderRadius="md"
                      bg={bgColor}
                    >
                      <VStack spacing={3} align="stretch">
                        {/* Sipariş Başlığı */}
                        <HStack justify="space-between" flexWrap="wrap">
                          <VStack align="start" spacing={0}>
                            <Text fontSize={{ base: "xs", sm: "sm" }} fontWeight="bold" color={textColor}>
                              Sipariş No: {order.orderNumber || 'N/A'}
                            </Text>
                            <Text fontSize={{ base: "2xs", sm: "xs" }} color="gray.400">
                              {new Date(order.createdAt).toLocaleString('tr-TR')}
                            </Text>
                            {/* Sipariş Veren */}
                            <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.600" mt={1}>
                              Sipariş Veren: {order.userName || order.user?.name || 'Misafir'}
                            </Text>
                          </VStack>
                          <Badge colorScheme={statusConfig.color} fontSize={{ base: "xs", sm: "sm" }}>
                            {statusConfig.label}
                          </Badge>
                        </HStack>

                        <Divider />

                        <VStack spacing={3} align="stretch">
                          {order.items?.map((item, itemIndex) => {
                            const hasModifications = 
                              (item.removedIngredients?.length > 0) || 
                              (item.selectedOptions?.length > 0) ||
                              (item.selectedExtras?.length > 0);
                            
                            return (
                              <Box
                                key={itemIndex}
                                p={3}
                                bg={hoverBg}
                                borderRadius="md"
                                borderLeft="3px solid"
                                borderColor={hasModifications ? "blue.400" : "gray.300"}
                              >
                                <Flex justify="space-between" align="start" mb={2}>
                                  <VStack align="start" spacing={1}>
                                    <Text fontWeight="bold" fontSize={{ base: "xs", sm: "sm" }} color={textColor}>
                                      {item.name} x{item.quantity}
                                    </Text>
                                    <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.600">
                                      {item.price?.toFixed(2) || '0.00'} ₺ x {item.quantity} = {(item.itemTotal || (item.price || 0) * item.quantity).toFixed(2)} ₺
                                    </Text>
                                  </VStack>
                                </Flex>
                                
                                {/* Seçenekler */}
                                {item.selectedOptions?.length > 0 && (
                                  <Flex align="center" gap={2} mt={2}>
                                    <Box w="2" h="2" borderRadius="full" bg="blue.500" />
                                    <Text color="blue.600" fontSize="xs" fontWeight="500">
                                      Seçenekler: {item.selectedOptions.map(option => {
                                        const name = option.name || option.optionName || '';
                                        const choiceName = option.choiceName || option.value || '';
                                        const price = option.price || 0;
                                        const priceText = price > 0 ? ` (+${price}₺)` : '';
                                        return `${name}: ${choiceName}${priceText}`;
                                      }).join(', ')}
                                    </Text>
                                  </Flex>
                                )}
                                
                                {/* Çıkarılanlar */}
                                {item.removedIngredients?.length > 0 && (
                                  <Flex align="center" gap={2} mt={2}>
                                    <Box w="2" h="2" borderRadius="full" bg="red.500" />
                                    <Text color="red.500" fontSize="xs">
                                      Çıkarılanlar: {item.removedIngredients.join(', ')}
                                    </Text>
                                  </Flex>
                                )}
                                
                                {/* Ekstralar */}
                                {item.selectedExtras?.length > 0 && (
                                  <Flex align="center" gap={2} mt={2}>
                                    <Box w="2" h="2" borderRadius="full" bg="green.500" />
                                    <Text color="green.500" fontSize="xs">
                                      Ekstralar: {item.selectedExtras.map(extra => 
                                        `${extra.name} (+${extra.price}₺)`
                                      ).join(', ')}
                                    </Text>
                                  </Flex>
                                )}
                              </Box>
                            );
                          })}
                        </VStack>

                        {/* Sipariş Notu */}
                        {order.notes && (
                          <Box
                            p={3}
                            bg="yellow.50"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="yellow.200"
                          >
                            <Flex align="center" gap={2}>
                              <Box w="2" h="2" borderRadius="full" bg="yellow.500" />
                              <Text color="yellow.700" fontSize="sm" fontWeight="medium">
                                Sipariş Notu: {order.notes}
                              </Text>
                            </Flex>
                          </Box>
                        )}

                        <Divider />

                        <HStack spacing={4} flexWrap="wrap">
                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>Ödeme Yöntemi</Text>
                            <Badge 
                              colorScheme={order.paymentMethod === 'app' ? 'green' : 'red'}
                              variant="solid"
                              p={1}
                              borderRadius="4px"
                              fontSize="xs"
                            >
                              {order.paymentMethod === 'app' ? 'Online Ödeme' : 'Nakit Ödeme'}
                            </Badge>
                          </Box>
                          
                          {order.menomCoinsUsed > 0 && (
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={1}>MenomCoin Kullanıldı</Text>
                              <Text fontSize="sm" fontWeight="bold" color="yellow.500">
                                {order.menomCoinsUsed} MenomCoin
                              </Text>
                            </Box>
                          )}

                          {order.tipAmount > 0 && (
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={1}>Bahşiş</Text>
                              <HStack spacing={1}>
                                <Text fontSize="sm" fontWeight="bold" color="green.500">
                                  {order.tipAmount.toFixed(2)} ₺
                                </Text>
                                {order.tipPercent && (
                                  <Text fontSize="xs" color="gray.500">
                                    (%{order.tipPercent})
                                  </Text>
                                )}
                              </HStack>
                            </Box>
                          )}

                          <Box>
                            <Text fontSize="xs" color="gray.500" mb={1}>Toplam Adet</Text>
                            <Text fontSize="sm" fontWeight="bold" color={textColor}>
                              {itemCount} ürün
                            </Text>
                          </Box>
                        </HStack>

                        <Divider />

                        {/* Sipariş Özeti */}
                        <VStack spacing={1} align="stretch">
                          {order.menomCoinsUsed > 0 && (
                            <>
                              <HStack justify="space-between">
                                <Text fontSize="sm" color="gray.600">Orijinal Tutar:</Text>
                                <Text fontSize="sm" textDecoration="line-through" color="gray.500">
                                  ₺{((order.totalAmount || 0) + (order.menomCoinsUsed || 0)).toFixed(2)}
                                </Text>
                              </HStack>
                              <HStack justify="space-between">
                                <Text fontSize="sm" color="gray.600">MenomCoin İndirimi:</Text>
                                <Text fontSize="sm" color="yellow.500" fontWeight="bold">
                                  -₺{order.menomCoinsUsed.toFixed(2)}
                                </Text>
                              </HStack>
                            </>
                          )}
                          <HStack justify="space-between">
                            <Text fontSize="sm" fontWeight="bold">Toplam:</Text>
                            <Text fontSize="sm" fontWeight="bold" color={textColor}>
                              ₺{orderTotal.toFixed(2)}
                            </Text>
                          </HStack>
                        </VStack>

                        {/* Durum Güncelleme - Görsel Vurgu ile */}
                        <Box
                          p={3}
                          bg={statusConfig.borderColor + '15'}
                          borderRadius="lg"
                          borderLeft="4px solid"
                          borderColor={statusConfig.borderColor}
                        >
                          <HStack justify="space-between" mb={2}>
                            <Text fontSize="sm" fontWeight="bold" color={textColor}>
                              Durum Güncelle:
                            </Text>
                            <Badge 
                              colorScheme={statusConfig.color} 
                              fontSize="sm"
                              px={3}
                              py={1}
                              borderRadius="full"
                              fontWeight="bold"
                            >
                              {statusConfig.label}
                            </Badge>
                          </HStack>
                          <VStack spacing={2} align="stretch">
                            <Select
                              value={order.status}
                              onChange={(e) => {
                                const newStatus = e.target.value;
                                if (newStatus === 'delivered' && employees.length > 0) {
                                  // Teslim edildi durumunda çalışan seçimi için state kullan
                                  setSelectedTable({
                                    ...selectedTable,
                                    pendingDelivery: {
                                      orderId: order._id || order.id,
                                      status: newStatus
                                    }
                                  });
                                } else if (newStatus === 'cancelled') {
                                  // İptal edildi durumu için direkt güncelle
                                  handleOrderStatusChange(order._id || order.id, newStatus);
                                } else if (newStatus !== 'delivered') {
                                  // Delivered dışındaki durumlar için direkt güncelle
                                  handleOrderStatusChange(order._id || order.id, newStatus);
                                }
                              }}
                              size="md"
                              fontWeight="semibold"
                              borderColor={statusConfig.borderColor}
                              _focus={{
                                borderColor: statusConfig.borderColor,
                                boxShadow: `0 0 0 1px ${statusConfig.borderColor}`
                              }}
                            >
                              {Object.entries(STATUS_CONFIG)
                                .filter(([key]) => key !== 'delivered') // Sadece teslim edildi durumunu kaldır (cancelled dahil)
                                .map(([key, config]) => (
                                  <option key={key} value={key}>
                                    {config.label}
                                  </option>
                                ))}
                              {/* Teslim Edildi seçeneğini en sona ekle */}
                              <option value="delivered">Teslim Edildi</option>
                            </Select>
                            
                            {/* Teslim edildi durumunda çalışan seçimi */}
                            {selectedTable?.pendingDelivery?.orderId === (order._id || order.id) && (
                              <VStack spacing={2} align="stretch">
                                <Text fontSize="xs" color="gray.500">
                                  Teslim Eden Çalışan:
                                </Text>
                                <Select
                                  placeholder="Çalışan seçin"
                                  size="sm"
                                  onChange={async (e) => {
                                    const employeeId = e.target.value;
                                    if (employeeId && employeeId !== '') {
                                      // Pending delivery state'ini önce temizle
                                      const orderId = selectedTable.pendingDelivery.orderId;
                                      const orderStatus = selectedTable.pendingDelivery.status;
                                      
                                      setSelectedTable(prev => {
                                        const { pendingDelivery, ...rest } = prev;
                                        return rest;
                                      });
                                      
                                      // Çalışan seçildikten sonra siparişi teslim et ve listeden çıkar
                                      await handleOrderStatusChange(
                                        orderId,
                                        orderStatus,
                                        employeeId
                                      );
                                    }
                                  }}
                                  autoFocus
                                >
                                  <option value="">Çalışan seçin</option>
                                  {employees.map(emp => (
                                    <option key={emp._id || emp.id} value={emp._id || emp.id}>
                                      {emp.name || 'Çalışan'}
                                    </option>
                                  ))}
                                </Select>
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedTable(prev => {
                                      const { pendingDelivery, ...rest } = prev;
                                      return rest;
                                    });
                                  }}
                                >
                                  İptal
                                </Button>
                              </VStack>
                            )}
                          </VStack>
                        </Box>
                      </VStack>
                    </Box>
                  );
                  })}
                </SimpleGrid>
              ) : (
                <Center py={10}>
                  <Text>Bu masada aktif sipariş bulunmuyor</Text>
                </Center>
              );
            })()}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
}

